// src/components/resources/calculators/IncrementalityCalculator.logic.ts
//
// Pure calculation logic for the incrementality calculator, extracted from the
// client component so it can be unit-tested without pulling the React/email
// (env) import graph. See IncrementalityCalculator.logic.test.ts.

import { formatPercent, formatRatio } from '~/lib/utils/formatters'

/**
 * Interface representing the user inputs for the incrementality calculator
 */
export interface CalculatorInputs {
  /** Size of the control group */
  controlGroupSize: number
  /** Number of conversions in the control group */
  controlGroupConversions: number
  /** Size of the test group */
  testGroupSize: number
  /** Number of conversions in the test group */
  testGroupConversions: number
  /** Average order value in dollars */
  averageOrderValue: number
  /** Total marketing cost in dollars */
  marketingCost: number
}

/**
 * Interface representing the calculated results from the incrementality test
 */
export interface CalculatorResults {
  /** Conversion rate of the control group (percentage) */
  controlConversionRate: number
  /** Conversion rate of the test group (percentage) */
  testConversionRate: number
  /** Absolute difference between test and control conversion rates (percentage points) */
  absoluteLift: number
  /** Relative lift as a percentage of the control conversion rate */
  relativeLift: number
  /** Number of conversions attributed to the marketing activity */
  incrementalConversions: number
  /** Revenue attributed to the marketing activity */
  incrementalRevenue: number
  /** Return on investment for the marketing activity (percentage) */
  incrementalROI: number
  /** Statistical confidence in the results (percentage) */
  statisticalSignificance: number
  /** Actionable recommendations based on the results */
  recommendations: Array<{
    type: 'primary' | 'optimization' | 'tip'
    text: string
    icon?: string
  }>
}

/**
 * Calculates incrementality metrics based on user inputs
 * @param {CalculatorInputs} inputs - User-provided test data
 * @returns {CalculatorResults} Calculated incrementality metrics and recommendations
 */
export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  // Ensure inputs are valid numbers
  const safeInputs = {
    controlGroupSize: Math.max(1, Number(inputs.controlGroupSize) || 0),
    controlGroupConversions: Math.max(0, Number(inputs.controlGroupConversions) || 0),
    testGroupSize: Math.max(1, Number(inputs.testGroupSize) || 0),
    testGroupConversions: Math.max(0, Number(inputs.testGroupConversions) || 0),
    averageOrderValue: Math.max(0, Number(inputs.averageOrderValue) || 0),
    marketingCost: Math.max(0, Number(inputs.marketingCost) || 0)
  }

  // Calculate conversion rates
  const controlConversionRate = (safeInputs.controlGroupConversions / safeInputs.controlGroupSize) * 100
  const testConversionRate = (safeInputs.testGroupConversions / safeInputs.testGroupSize) * 100

  // Calculate lift
  const absoluteLift = testConversionRate - controlConversionRate
  const relativeLift = controlConversionRate === 0 ? 0 : (absoluteLift / controlConversionRate) * 100

  // Calculate incremental conversions
  // What would have happened at control rate vs what actually happened
  const expectedConversions = safeInputs.testGroupSize * (controlConversionRate / 100)
  const incrementalConversions = safeInputs.testGroupConversions - expectedConversions

  // Calculate incremental revenue
  const incrementalRevenue = incrementalConversions * safeInputs.averageOrderValue

  // Calculate ROI
  const incrementalROI = safeInputs.marketingCost === 0 ? 0 : (incrementalRevenue / safeInputs.marketingCost) * 100

  // Calculate statistical significance using z-test
  // For simplicity, we're using a basic z-test for proportions
  const p1 = safeInputs.controlGroupConversions / safeInputs.controlGroupSize
  const p2 = safeInputs.testGroupConversions / safeInputs.testGroupSize
  const p = (safeInputs.controlGroupConversions + safeInputs.testGroupConversions) /
            (safeInputs.controlGroupSize + safeInputs.testGroupSize)
  const se = Math.sqrt(p * (1 - p) * (1/safeInputs.controlGroupSize + 1/safeInputs.testGroupSize))
  const z = isNaN(se) || se === 0 ? 0 : Math.abs((p1 - p2) / se)

  // Two-tailed CONFIDENCE that the lift is real: P(|Z| < z) = 2·Φ(z) − 1
  // (normalCDF is Φ; z is already |Δp / se|). The previous `(1 - normalCDF(z))`
  // computed the one-tailed p-VALUE and reported it AS the confidence — inverted,
  // so a strongly-significant test (large z) showed ~0% and told users to collect
  // more data. Matches the two-tailed convention of ABTestCalculator and this
  // tool's own "p < 0.05 (95% confidence)" guidance.
  const statisticalSignificance = Math.min(99.9, Math.max(0, (2 * normalCDF(z) - 1) * 100))

  // Generate recommendations
  const recommendations = generateRecommendations(
    relativeLift,
    incrementalROI,
    statisticalSignificance,
    safeInputs.controlGroupSize,
    safeInputs.testGroupSize
  )

  return {
    controlConversionRate: isFinite(controlConversionRate) ? controlConversionRate : 0,
    testConversionRate: isFinite(testConversionRate) ? testConversionRate : 0,
    absoluteLift: isFinite(absoluteLift) ? absoluteLift : 0,
    relativeLift: isFinite(relativeLift) ? relativeLift : 0,
    incrementalConversions: isFinite(incrementalConversions) ? incrementalConversions : 0,
    incrementalRevenue: isFinite(incrementalRevenue) ? incrementalRevenue : 0,
    incrementalROI: isFinite(incrementalROI) ? incrementalROI : 0,
    statisticalSignificance: isFinite(statisticalSignificance) ? statisticalSignificance : 0,
    recommendations
  }
}

/**
 * Helper function for normal cumulative distribution function
 * @param {number} x - Z-score value
 * @returns {number} Probability value
 */
export function normalCDF(x: number): number {
  if (!isFinite(x)) return x > 0 ? 1 : 0
  const t = 1 / (1 + 0.2316419 * Math.abs(x))
  const d = 0.3989423 * Math.exp(-x * x / 2)
  const probability = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))))
  return x > 0 ? 1 - probability : probability
}

/**
 * Generates actionable recommendations based on test results
 * @param {number} relativeLift - Relative lift percentage
 * @param {number} incrementalROI - Incremental ROI percentage
 * @param {number} statisticalSignificance - Statistical significance percentage
 * @param {number} controlSize - Size of control group
 * @param {number} testSize - Size of test group
 * @returns {Array<{type: 'primary' | 'optimization' | 'tip', text: string, icon?: string}>} Array of recommendation objects
 */
function generateRecommendations(
  relativeLift: number,
  incrementalROI: number,
  statisticalSignificance: number,
  controlSize: number,
  testSize: number
): Array<{type: 'primary' | 'optimization' | 'tip'; text: string; icon?: string}> {
  const recommendations: Array<{type: 'primary' | 'optimization' | 'tip'; text: string; icon?: string}> = []

  // These metrics are WHOLE percents (e.g. 95 = 95%), but `formatPercent` expects
  // a FRACTION (0.95 → "95.0%"). Wrap so the display scale is right — passing the
  // whole percent rendered 100× too large (e.g. "9717.1%"), and callers also
  // appended a literal "%" on top of formatPercent's own, producing "%%". `pct`
  // emits the "%" itself, so the call sites must NOT append another.
  const pct = (wholePercent: number): string => formatPercent(wholePercent / 100)

  // Statistical significance recommendation
  if (statisticalSignificance < 90) {
    recommendations.push({
      type: 'primary',
      text: `Your test has only ${pct(statisticalSignificance)} statistical significance. Consider increasing sample sizes or running the test longer to achieve at least 95% confidence.`,
      icon: 'warning'
    })
  } else {
    recommendations.push({
      type: 'primary',
      text: `Your test has achieved ${pct(statisticalSignificance)} statistical significance, providing reliable results for decision-making.`,
      icon: 'chart'
    })
  }

  // Sample size balance recommendation
  const sizeRatio = Math.max(controlSize, testSize) / Math.min(controlSize, testSize)
  if (sizeRatio > 1.5) {
    recommendations.push({
      type: 'optimization',
      text: `Your test and control groups have a size imbalance (ratio: ${formatRatio(sizeRatio)}:1). For future tests, aim for more balanced group sizes to improve statistical power.`,
      icon: 'attribution'
    })
  }

  // Lift recommendations
  if (relativeLift <= 0) {
    recommendations.push({
      type: 'primary',
      text: `Your marketing activity shows no positive lift (${pct(relativeLift)}). Consider revising your strategy or testing different approaches.`,
      icon: 'warning'
    })
  } else if (relativeLift < 10) {
    recommendations.push({
      type: 'optimization',
      text: `Your relative lift of ${pct(relativeLift)} is modest. Test creative variations or targeting refinements to improve performance.`,
      icon: 'attribution'
    })
  } else if (relativeLift >= 10 && relativeLift < 30) {
    recommendations.push({
      type: 'primary',
      text: `Your relative lift of ${pct(relativeLift)} shows good incremental impact. Consider scaling this marketing activity.`,
      icon: 'chart'
    })
  } else {
    recommendations.push({
      type: 'primary',
      text: `Your exceptional relative lift of ${pct(relativeLift)} indicates a highly effective marketing activity. Prioritize scaling this approach.`,
      icon: 'chart'
    })
  }

  // ROI recommendations
  if (incrementalROI < 100) {
    recommendations.push({
      type: 'optimization',
      text: `Your incremental ROI of ${pct(incrementalROI)} is below 100%, meaning you're not recovering your full marketing investment through incremental revenue. Consider optimizing costs or targeting.`,
      icon: 'strategy'
    })
  } else {
    recommendations.push({
      type: 'tip',
      text: `Your incremental ROI of ${pct(incrementalROI)} shows positive return on your marketing investment. Continue monitoring and optimizing for even better results.`,
      icon: 'strategy'
    })
  }

  return recommendations
}
